import { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import './AIAssistant.css'
import aiAssist1 from '../../assets/ai_assist/ai_assist_1.png'
import aiAssist2 from '../../assets/ai_assist/ai_assist_2.png'
import aiAssist3 from '../../assets/ai_assist/ai_assist_3.png'
import aiAssist4 from '../../assets/ai_assist/ai_assist_4.png'
import aiAssist5 from '../../assets/ai_assist/ai_assist_5.png'
import aiAssist6 from '../../assets/ai_assist/ai_assist_6.png'
import aiAssist7 from '../../assets/ai_assist/ai_assist_7.png'
import icClose from '../../assets/ic_close.png'

interface AIAssistantProps {
  speechText?: string
  audioUrl?: string
}

export interface AIAssistantRef {
  close: () => void
}

const AIAssistant = forwardRef<AIAssistantRef, AIAssistantProps>(({ speechText, audioUrl }, ref) => {
  const { t } = useTranslation()
  const [currentFrame, setCurrentFrame] = useState(0)
  const [isExpanded, setIsExpanded] = useState(false)
  const [displayedText, setDisplayedText] = useState('')
  const [audioLevel, setAudioLevel] = useState(0) // Уровень аудио для анимации
  const [restartTyping, setRestartTyping] = useState(0) // Флаг для перезапуска печати
  const audioRef = useRef<HTMLAudioElement>(null)
  const typingIntervalRef = useRef<number | null>(null)
  // Временно отключены refs для Web Audio API анализа
  // const audioContextRef = useRef<AudioContext | null>(null)
  // const analyserRef = useRef<AnalyserNode | null>(null)
  // const animationFrameRef = useRef<number | null>(null)
  // const sourceRef = useRef<MediaElementAudioSourceNode | null>(null)
  
  const mockText = `Alright, I went over the front end.
Here's what I'm seeing.

The stabilizer link is knocking. You can hear it when you hit bumps or speed bumps. That little rod is worn out and needs to be replaced.

Also, the brake pads are almost gone. There's not much material left, and if you keep driving like this, it'll start squealing and chewing into the discs.

The rest looks fine.

The brake discs are smooth, no big lip on the edge.
The rubber brake lines look healthy, no cracks or swelling.
The CV boots are dry, no grease slung around.

Car is drivable for now, but fixing those two things will save you from bigger headaches later.`
  
  const fullText = speechText || mockText
  
  const frames = [
    aiAssist1,
    aiAssist2,
    aiAssist3,
    aiAssist4,
    aiAssist5,
    aiAssist6,
    aiAssist7,
  ]

  useEffect(() => {
    // Анимация: плавное переключение между кадрами (только когда не расширено)
    if (!isExpanded) {
      const interval = setInterval(() => {
        setCurrentFrame((prev) => (prev + 1) % frames.length)
      }, 150) // Меняем кадр каждые 150ms

      return () => clearInterval(interval)
    }
  }, [frames.length, isExpanded])

  // Анимация печатания текста, синхронизированная с длительностью аудио
  useEffect(() => {
    if (isExpanded && audioRef.current && fullText.length > 0) {
      setDisplayedText('')
      let currentIndex = 0
      
      // Вычисляем скорость печатания на основе длительности аудио
      const startTyping = () => {
        const duration = audioRef.current?.duration
        let typingSpeed = 30 // Значение по умолчанию
        
        if (duration && duration > 0 && !isNaN(duration)) {
          // Calculate interval so the full text types over the audio duration
          typingSpeed = (duration * 1000) / fullText.length
          typingSpeed = Math.max(10, Math.min(100, typingSpeed))
        }
        
        // Очищаем предыдущий интервал, если есть
        if (typingIntervalRef.current) {
          window.clearInterval(typingIntervalRef.current)
        }
        
        typingIntervalRef.current = window.setInterval(() => {
          if (currentIndex < fullText.length) {
            setDisplayedText(fullText.slice(0, currentIndex + 1))
            currentIndex++
          } else {
            if (typingIntervalRef.current) {
              window.clearInterval(typingIntervalRef.current)
            }
          }
        }, typingSpeed)
      }
      
      // Если длительность уже загружена, начинаем сразу
      if (audioRef.current.duration && !isNaN(audioRef.current.duration)) {
        startTyping()
      } else {
        // Ждем загрузки метаданных
        const handleLoadedMetadata = () => {
          startTyping()
          audioRef.current?.removeEventListener('loadedmetadata', handleLoadedMetadata)
        }
        audioRef.current.addEventListener('loadedmetadata', handleLoadedMetadata)
        
        return () => {
          audioRef.current?.removeEventListener('loadedmetadata', handleLoadedMetadata)
          if (typingIntervalRef.current) {
            window.clearInterval(typingIntervalRef.current)
          }
        }
      }
      
      return () => {
        if (typingIntervalRef.current) {
          window.clearInterval(typingIntervalRef.current)
        }
      }
    } else {
      setDisplayedText('')
    }
  }, [isExpanded, fullText, restartTyping])

  const handleCircleClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Предотвращаем всплытие события
    if (isExpanded) {
      // Если уже расширено, начинаем заново и аудио, и печать
      setDisplayedText('')
      setRestartTyping(prev => prev + 1) // Триггерим перезапуск печати
      if (audioRef.current) {
        audioRef.current.currentTime = 0
        const playPromise = audioRef.current.play()
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.error('[ERROR] Audio playback failed:', error)
          })
        }
      }
    } else {
      // Расширяем - воспроизведение начнется в useEffect
      setIsExpanded(true)
    }
  }

  const closeAssistant = () => {
    setIsExpanded(false)
    setDisplayedText('')
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    if (typingIntervalRef.current) {
      window.clearInterval(typingIntervalRef.current)
    }
    setAudioLevel(0)
  }

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation() // Предотвращаем срабатывание клика на круглишок
    closeAssistant()
  }

  // Предоставляем метод close через ref
  useImperativeHandle(ref, () => ({
    close: closeAssistant
  }))

  // Обновление src аудио при изменении audioUrl
  useEffect(() => {
    if (audioRef.current && audioUrl) {
      // Сбрасываем текущее время и загружаем новый источник
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      audioRef.current.src = audioUrl
      audioRef.current.load() // Принудительно загружаем новый источник
      
      const handleError = (e: Event) => {
        const audioElement = e.target as HTMLAudioElement
        console.error('[ERROR] Audio load failed — url:', audioUrl, 'code:', audioElement.error?.code, audioElement.error?.message)
      }

      const handleCanPlay = () => {}

      const handleLoadStart = () => {}

      audioRef.current.addEventListener('error', handleError)
      audioRef.current.addEventListener('canplay', handleCanPlay)
      audioRef.current.addEventListener('loadstart', handleLoadStart)

      return () => {
        audioRef.current?.removeEventListener('error', handleError)
        audioRef.current?.removeEventListener('canplay', handleCanPlay)
        audioRef.current?.removeEventListener('loadstart', handleLoadStart)
      }
    }
  }, [audioUrl])

  // Автоматическое воспроизведение при расширении
  useEffect(() => {
    if (isExpanded && audioRef.current && audioUrl) {
      const audioElement = audioRef.current

      audioElement.volume = 1.0
      audioElement.muted = false

      const playAudio = async () => {
        try {
          if (audioElement.readyState >= 2) {
            audioElement.currentTime = 0
            const playPromise = audioElement.play()
            if (playPromise !== undefined) {
              await playPromise
            }
          } else {
            const handleCanPlay = async () => {
              if (audioRef.current) {
                audioRef.current.volume = 1.0
                audioRef.current.muted = false
                audioRef.current.currentTime = 0
                const playPromise = audioRef.current.play()
                if (playPromise !== undefined) {
                  await playPromise
                }
                audioRef.current.removeEventListener('canplay', handleCanPlay)
              }
            }
            audioElement.addEventListener('canplay', handleCanPlay)
            audioElement.load()
          }
        } catch (error) {
          console.error('[ERROR] Audio playback failed:', error)
        }
      }
      playAudio()
    }
  }, [isExpanded, audioUrl])

  // Настройка обработчиков событий аудио (без Web Audio API анализа)
  // ВРЕМЕННО: отключен анализ из-за проблем с createMediaElementSource и внешними URL
  useEffect(() => {
    if (isExpanded && audioRef.current) {
      // Просто слушаем события воспроизведения без анализа
      const handlePlay = () => {
        setAudioLevel(0.3)
      }

      const handlePause = () => {
        setAudioLevel(0)
      }

      const handleEnded = () => {
        // После завершения аудио оставляем плашку открытой, просто сбрасываем уровень
        setAudioLevel(0)
      }

      audioRef.current.addEventListener('play', handlePlay)
      audioRef.current.addEventListener('pause', handlePause)
      audioRef.current.addEventListener('ended', handleEnded)

      return () => {
        audioRef.current?.removeEventListener('play', handlePlay)
        audioRef.current?.removeEventListener('pause', handlePause)
        audioRef.current?.removeEventListener('ended', handleEnded)
        setAudioLevel(0)
      }
    } else {
      setAudioLevel(0)
    }
  }, [isExpanded])

  return (
    <div className="ai-assistant">
      <div className={`ai-assistant__container ${isExpanded ? 'ai-assistant__container--expanded' : ''}`}>
        {!isExpanded && (
          <>
            <span className="ai-assistant__text ai-assistant__text--left">
              {t('ai_assistant.advises_from')}
            </span>
            <div className="ai-assistant__circle" onClick={handleCircleClick}>
              <img
                key={currentFrame}
                src={frames[currentFrame]}
                alt={t('ai_assistant.title')}
                className="ai-assistant__circle-image"
              />
            </div>
            <span className="ai-assistant__text ai-assistant__text--right">
              {t('ai_assistant.title')}
            </span>
          </>
        )}
        
        {isExpanded && (
          <>
            <div 
              className="ai-assistant__circle ai-assistant__circle--expanded" 
              onClick={handleCircleClick}
              style={{
                transform: `translate(-50%, -50%) scale(${1 + audioLevel * 0.15})`,
                filter: `brightness(${1 + audioLevel * 0.2})`,
              }}
            >
              <img
                src={frames[0]} // Показываем первый кадр когда расширено
                alt={t('ai_assistant.title')}
                className="ai-assistant__circle-image"
              />
            </div>
            <div className="ai-assistant__message">
              <p className="ai-assistant__message-text">
                {displayedText}
                <span className="ai-assistant__cursor">|</span>
              </p>
            </div>
            <button
              className="ai-assistant__close"
              onClick={handleClose}
              aria-label={t('ai_assistant.close')}
            >
              <img src={icClose} alt={t('ai_assistant.close')} className="ai-assistant__close-icon" />
            </button>
          </>
        )}
      </div>
      {audioUrl && (
        <audio 
          ref={audioRef} 
          preload="auto"
        />
      )}
    </div>
  )
})

AIAssistant.displayName = 'AIAssistant'

export default AIAssistant
